import { useState, type FormEvent } from 'react';
import { Box, Button, Card, Center, Image, Stack, Text, TextInput } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset } from '@/api/auth';
import { usePageTitle } from '@/components/usePageTitle';
import { forest } from '@/theme/theme';

export const Route = createFileRoute('/reset-password')({ component: ResetPasswordPage });

function ResetPasswordPage() {
  const { t } = useTranslation();
  usePageTitle(t('auth.resetTitle'));
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch {
      // This endpoint has no distinct failure codes of its own (it 202s even for an
      // unknown email, per the backend); any error here is a transport or malformed-
      // input failure, so the generic message is the only one that applies.
      setError(t('auth.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box pos="relative" h="100vh" bg={forest[9]} style={{ overflow: 'hidden' }}>
      <Box
        pos="absolute"
        inset={0}
        style={{
          backgroundImage: 'url(/rastr.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.28,
        }}
      />
      <Center pos="relative" h="100%">
        <Stack align="center" gap="xl">
          <Image src="/logo-white.png" alt="Forestum" w={160} fit="contain" />
          <Card w={360}>
            {sent ? (
              <Text>{t('auth.resetSent')}</Text>
            ) : (
              <form onSubmit={(e) => void handleSubmit(e)}>
                <Stack gap="md">
                  <TextInput
                    label={t('auth.email')}
                    type="email"
                    autoComplete="username"
                    required
                    withAsterisk={false}
                    value={email}
                    onChange={(event) => setEmail(event.currentTarget.value)}
                  />
                  {error && (
                    <Text c="red" role="alert">
                      {error}
                    </Text>
                  )}
                  <Button type="submit" fullWidth loading={submitting}>
                    {t('auth.resetTitle')}
                  </Button>
                </Stack>
              </form>
            )}
          </Card>
        </Stack>
      </Center>
    </Box>
  );
}
